import {
  Bell,
  ChevronDown,
  FileSignature,
  LogOut,
  Menu,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthContext";
import { initials } from "../lib/format";
import { Brand } from "./Brand";

const navigation = [
  { label: "Disclosures", to: "/disclosures", icon: FileSignature },
  { label: "Members", to: "/members", icon: Users },
];

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="mt-7 space-y-1" aria-label="Primary navigation">
      {navigation.map(({ label, to, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-bold transition ${
              isActive
                ? "bg-brand text-ink"
                : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
            }`
          }
        >
          <Icon className="size-5" strokeWidth={1.9} aria-hidden="true" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-ink p-5 text-white lg:flex">
        <Brand />
        <Navigation />
        <div className="mt-auto border-t border-neutral-800 pt-5">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-bold ${
                isActive
                  ? "bg-brand text-ink"
                  : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
              }`
            }
          >
            <Settings className="size-5" aria-hidden="true" />
            Settings
          </NavLink>
          <p className="mt-5 px-3 text-[11px] leading-5 text-neutral-500">
            Secure operations workspace
            <br />
            C&amp;R Mortgage Group
          </p>
        </div>
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative flex h-full w-[min(84vw,20rem)] flex-col bg-ink p-5 text-white shadow-2xl">
            <div className="flex items-center justify-between">
              <Brand />
              <button
                type="button"
                className="grid size-10 place-items-center rounded-lg text-neutral-300 hover:bg-neutral-800"
                aria-label="Close navigation"
                onClick={() => setMobileOpen(false)}
              >
                <X className="size-5" />
              </button>
            </div>
            <Navigation onNavigate={() => setMobileOpen(false)} />
            <NavLink
              to="/settings"
              onClick={() => setMobileOpen(false)}
              className="mt-auto flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-bold text-neutral-300 hover:bg-neutral-800"
            >
              <Settings className="size-5" />
              Settings
            </NavLink>
          </aside>
        </div>
      ) : null}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center border-b border-neutral-200 bg-white/95 px-4 backdrop-blur sm:px-6 lg:px-8">
          <button
            type="button"
            className="mr-3 grid size-10 place-items-center rounded-lg hover:bg-neutral-100 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="size-5" />
          </button>
          <div className="hidden text-xs font-bold uppercase tracking-[0.17em] text-neutral-500 sm:block">
            Mortgage operations
          </div>
          <div className="ml-auto flex items-center gap-2">
            {user?.isDemo ? (
              <span className="hidden rounded-md border border-amber-300 bg-brand-soft px-2 py-1 text-xs font-bold text-amber-950 sm:inline-flex">
                Demo workspace
              </span>
            ) : null}
            <button
              type="button"
              className="relative grid size-10 place-items-center rounded-lg text-neutral-600 hover:bg-neutral-100"
              aria-label="Notifications"
            >
              <Bell className="size-5" />
              <span className="absolute right-2.5 top-2.5 size-2 rounded-full bg-brand ring-2 ring-white" />
            </button>
            <div className="relative">
              <button
                type="button"
                className="flex min-h-11 items-center gap-2 rounded-lg px-1.5 hover:bg-neutral-100"
                onClick={() => setUserMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <span className="grid size-8 place-items-center rounded-full bg-ink text-xs font-extrabold text-white">
                  {initials(user?.name ?? "CR")}
                </span>
                <span className="hidden text-left md:block">
                  <span className="block max-w-40 truncate text-sm font-bold">{user?.name}</span>
                  <span className="block text-[11px] text-neutral-500">{user?.role}</span>
                </span>
                <ChevronDown className="hidden size-4 text-neutral-500 md:block" />
              </button>
              {userMenuOpen ? (
                <div
                  className="card absolute right-0 top-12 w-64 rounded-xl p-2 shadow-xl"
                  role="menu"
                >
                  <div className="border-b border-neutral-100 px-3 py-2">
                    <p className="truncate text-sm font-bold">{user?.email}</p>
                    <p className="mt-0.5 text-xs text-neutral-500">{user?.organizationName}</p>
                  </div>
                  <button
                    type="button"
                    className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-bold text-neutral-700 hover:bg-neutral-100"
                    role="menuitem"
                    onClick={handleSignOut}
                  >
                    <LogOut className="size-4" />
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1500px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
